import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type Mode = "login" | "signup";

export default function Auth() {
  const [mode, setMode] = useState<Mode>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/member";

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (mode === "signup") {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { name },
          },
        });
        if (signUpError) {
          setError(signUpError.message);
          return;
        }
        if (data.user) {
          await supabase.from("profiles").upsert({
            id: data.user.id,
            name,
            email,
            points: 0,
            level: "普通會員",
          });
        }
        setMessage("註冊成功！請檢查你的電子郵件完成驗證，然後登入。");
        setMode("login");
        setPassword("");
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) {
          setError(signInError.message);
          return;
        }
        navigate(redirect);
      }
    } catch {
      setError("發生錯誤，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-50/60 text-brand-900">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="max-w-md mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">
              {mode === "login" ? "會員登入" : "註冊會員"}
            </div>
            <h1 className="mt-5 font-serif text-3xl md:text-4xl text-brand-800">
              {mode === "login" ? "歡迎回來" : "成為 ISHB 會員"}
            </h1>
            <p className="mt-4 text-brand-700/70 text-sm">
              {mode === "login"
                ? "登入後即可查看報名紀錄、收藏課程與專屬會員優惠。"
                : "註冊即可累積點數、解鎖會員等級與專屬優惠。"}
            </p>
          </div>

          <div className="bg-white border border-brand-100 rounded-lg p-6 md:p-8">
            <div className="flex p-1 bg-brand-50 rounded-full mb-6">
              <button
                onClick={() => { setMode("login"); setError(""); setMessage(""); }}
                className={`flex-1 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition ${
                  mode === "login" ? "bg-brand-600 text-white" : "text-brand-700"
                }`}
              >
                登入
              </button>
              <button
                onClick={() => { setMode("signup"); setError(""); setMessage(""); }}
                className={`flex-1 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition ${
                  mode === "signup" ? "bg-brand-600 text-white" : "text-brand-700"
                }`}
              >
                註冊
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {mode === "signup" && (
                <div>
                  <label htmlFor="auth-name" className="block text-sm font-medium text-brand-900 mb-1.5">姓名 *</label>
                  <input
                    id="auth-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="你的姓名"
                    className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  />
                </div>
              )}

              <div>
                <label htmlFor="auth-email" className="block text-sm font-medium text-brand-900 mb-1.5">電郵地址 *</label>
                <input
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                />
              </div>

              <div>
                <label htmlFor="auth-password" className="block text-sm font-medium text-brand-900 mb-1.5">密碼 *</label>
                <input
                  id="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="至少 6 個字元"
                  className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {message && (
                <p className="text-sm text-brand-600">{message}</p>
              )}
              {error && (
                <p className="text-sm text-rose-500">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 處理中...
                  </>
                ) : mode === "login" ? (
                  "登入"
                ) : (
                  "註冊"
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-brand-700/70">
              {mode === "login" ? "還沒有帳號？" : "已經有帳號？"}
              <button
                onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); setMessage(""); }}
                className="ml-1 text-brand-600 font-medium cursor-pointer"
              >
                {mode === "login" ? "立即註冊" : "立即登入"}
              </button>
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-brand-700/50">
            尚未登入？<Link to="/courses" className="text-brand-600 cursor-pointer">先瀏覽最新課程</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}